import { useMemo, useState } from "react";
import { HiddenWhenSpiffOnly } from "@/core/components/hidden-when-spiff-only";
import {
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@/core/components/ui/simple-sidebar";
import { Stack } from "@/core/components/ui/stack/stack";
import {
  useGetBusinessStats,
  useGetBusinessWeeklyStats,
} from "@/business/query";
import { useBusiness } from "@/core/business";
import { NoReferrals } from "@/referrals/no-referrals";
import { IMostRecentTextMessage } from "@/text-messages/text-messages";
import { DashboardSidebar } from "./dashboard-sidebar";
import ParticipationTrackerPane from "./participation-tracker-pane/participation-tracker-pane";
import ReferralsChartPane from "./referrals-chart-pane";
import SignupsChartPane from "./signups-chart-pane";
import TextMessagesPane from "./text-messages-pane";

const DashboardContent = () => {
  const { businessId } = useBusiness();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<IMostRecentTextMessage | null>(null);

  const { data: businessStats } = useGetBusinessStats(businessId);
  const { data: weeklyStats, isLoading } =
    useGetBusinessWeeklyStats(businessId);

  const hasNoActivity = useMemo(
    () =>
      !weeklyStats?.some((stat) => stat.referral_count > 0) &&
      !weeklyStats?.some((stat) => stat.total_signup_count > 0),
    [weeklyStats, businessStats],
  );

  if (hasNoActivity && !isLoading) {
    return <NoReferrals />;
  }

  return (
    <SimpleSidebarLayout
      open={isSidebarOpen}
      onOpenChange={(open) => {
        setSelectedItem(open ? selectedItem : null);
        setIsSidebarOpen(open);
      }}
      sidebarWidth="436px"
    >
      <SimpleSidebarContent className="h-full">
        <Stack
          direction="row"
          className="min-w-[1200px] p-8 pt-6"
          gap="lg"
          fullWidth
        >
          <Stack gap="lg" fullWidth>
            <Stack direction="row" gap="lg" align="stretch" fullWidth>
              <SignupsChartPane />
              <ReferralsChartPane />
            </Stack>
            <TextMessagesPane
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              setIsSidebarOpen={setIsSidebarOpen}
            />
          </Stack>
          <ParticipationTrackerPane />
        </Stack>
      </SimpleSidebarContent>
      <DashboardSidebar
        isOpen={isSidebarOpen}
        selectedItem={selectedItem}
        setIsSidebarOpen={setIsSidebarOpen}
      />
    </SimpleSidebarLayout>
  );
};

const Dashboard = () => (
  <HiddenWhenSpiffOnly>
    <DashboardContent />
  </HiddenWhenSpiffOnly>
);

export default Dashboard;
